import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";

const toLocalDt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalDt(d.toISOString());
};

const buildEmpty = () => ({
  name: "", code: "", type: "percentage", value: 10,
  product_ids: [],
  start_date: todayPlus(0),
  end_date: todayPlus(30),
  active: true,
  featured: false,
  image_url: "",
  description: "",
});

export default function Promotions() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(buildEmpty());
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/promotions").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        value: Number(form.value) || 0,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
      };
      if (editing) await api.put(`/promotions/${editing}`, payload);
      else await api.post("/promotions", payload);
      toast.success(editing ? "Promoción actualizada" : "Promoción creada");
      setForm(buildEmpty()); setEditing(null); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onEdit = (p) => {
    setForm({
      ...p,
      start_date: toLocalDt(p.start_date),
      end_date: toLocalDt(p.end_date),
      product_ids: p.product_ids || [],
    });
    setEditing(p.id); setShowForm(true);
  };
  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar promoción?")) return;
    try { await api.delete(`/promotions/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="promotions-page">
      <PageHeader
        title="Promociones"
        subtitle="Descuentos por porcentaje o monto para productos seleccionados."
        testid="promotions-header"
        action={
          <button data-testid="promotions-create-button"
            onClick={() => { setForm(buildEmpty()); setEditing(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nueva promoción
          </button>
        }
      />

      {showForm && (
        <div data-testid="promotion-form" className="card-soft p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nueva"} promoción</h3>
            <button onClick={() => setShowForm(false)} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre *"><input data-testid="promotion-name" required className="input-soft" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Código (opcional)"><input className="input-soft" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Tipo</span>
              <select className="input-soft" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="percentage">Porcentaje</option>
                <option value="amount">Monto</option>
              </select>
            </label>
            <Field label={form.type === "percentage" ? "Valor (%)" : "Valor ($)"}><input type="number" step="0.01" className="input-soft" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></Field>
            <Field label="Inicio *"><input type="datetime-local" required className="input-soft" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Fin *"><input type="datetime-local" required className="input-soft" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
            <div className="md:col-span-2">
              <Field label="Descripción (visible en landing pública)">
                <textarea rows={2} className="input-soft" value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="URL de imagen (opcional)">
                <input className="input-soft" placeholder="https://..." value={form.image_url || ""}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              </Field>
            </div>
            <label className="flex items-center gap-2">
              <input data-testid="promotion-featured" type="checkbox" checked={!!form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
              <span className="text-sm">Destacada en la landing pública</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.active !== false}
                onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              <span className="text-sm">Activa</span>
            </label>
            <div className="md:col-span-2">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Productos (deja vacío para aplicar a todos)</span>
                <select multiple className="input-soft min-h-[120px]" value={form.product_ids}
                  onChange={(e) => setForm({ ...form, product_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
            </div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button data-testid="promotion-save-button" className="btn-primary" type="submit">Guardar</button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="Sin promociones" hint="Crea descuentos para impulsar ventas." testid="promotions-empty" />
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="promotions-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
                <th className="px-5 py-3">Nombre</th><th className="px-5 py-3">Código</th>
                <th className="px-5 py-3">Tipo</th><th className="px-5 py-3">Valor</th>
                <th className="px-5 py-3">Vigencia</th><th className="px-5 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                  <td className="px-5 py-4 font-medium">
                    <div className="flex items-center gap-2">
                      {p.name}
                      {p.featured && (
                        <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#E4D5C7] text-[#2A3F35]">Destacada</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4 font-mono">{p.code || "—"}</td>
                  <td className="px-5 py-4 capitalize text-[#5C6B64]">{p.type === "percentage" ? "Porcentaje" : "Monto"}</td>
                  <td className="px-5 py-4">{p.type === "percentage" ? `${p.value}%` : `$ ${Number(p.value).toFixed(2)}`}</td>
                  <td className="px-5 py-4 text-xs text-[#5C6B64]">
                    {new Date(p.start_date).toLocaleDateString()} → {new Date(p.end_date).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => onEdit(p)} className="p-2 rounded hover:bg-[#F3F2ED]"><Pencil className="w-4 h-4 text-[#5C6B64]" /></button>
                      <button onClick={() => onDelete(p.id)} className="p-2 rounded hover:bg-[#F3DADA]"><Trash2 className="w-4 h-4 text-[#A44A4A]" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
